import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { cardPath } from '@/lib/cardSlug';
import { LoadingSpinner } from '@/components/LoadingSpinner';

const SITE = 'https://lorescout.com';

interface BannedCard {
  id: string;
  name: string;
  subtitle: string | null;
  set_name: string;
  rarity: string;
  legality_core: string;
}

export default function BannedPage() {
  const { data: cards, isLoading } = useQuery({
    queryKey: ['banned-cards'],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cards')
        .select('id,name,subtitle,set_name,rarity,legality_core')
        .eq('legality_core', 'banned')
        .order('name');
      if (error) throw error;
      return (data || []) as BannedCard[];
    },
  });

  const description = cards?.length
    ? `${cards.length} Disney Lorcana cards are currently banned in Core format: ${cards.slice(0, 6).map(c => c.name).join(', ')}. Full list and details on LoreScout.`
    : 'Current Disney Lorcana banned card list. No cards are currently banned in Core format.';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Disney Lorcana Banned Cards (Core format)',
    numberOfItems: cards?.length || 0,
    itemListElement: (cards || []).map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE}${cardPath(c)}`,
      name: c.subtitle ? `${c.name} — ${c.subtitle}` : c.name,
    })),
  };

  return (
    <div className="app-container min-h-screen pb-12">
      <Helmet>
        <title>Lorcana Banned Cards — Current Core Banlist · LoreScout</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${SITE}/banned`} />
        <meta property="og:title" content="Disney Lorcana Banned Cards — Core Format" />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`${SITE}/banned`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="flex items-center gap-3 p-4 border-b border-border">
        <Link to="/" className="text-sm underline">← LoreScout</Link>
      </header>

      <article className="px-4 pt-4">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link>
          {' / '}<span className="text-foreground">Banned Cards</span>
        </nav>
        <h1 className="font-serif text-3xl">Lorcana Banned Cards</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Cards currently <strong>banned in the Core format</strong> of Disney Lorcana. Updated alongside Ravensburger's
          official banlist. Cards not on this list are legal in Core.
        </p>

        {isLoading && <div className="py-12 flex justify-center"><LoadingSpinner /></div>}

        {!isLoading && cards && cards.length === 0 && (
          <section className="mt-8 rounded-lg border border-border p-6 text-center">
            <p className="text-lg font-medium">No cards are currently banned in Core.</p>
            <p className="text-sm text-muted-foreground mt-2">
              See <Link to="/rotation" className="underline">rotation</Link> for the current Core-legal set list.
            </p>
          </section>
        )}

        {!!cards?.length && (
          <section className="mt-6">
            <ul className="space-y-2">
              {cards.map(c => (
                <li key={c.id}>
                  <Link to={cardPath(c)} className="flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 hover:bg-destructive/10">
                    <span>
                      <span className="font-medium">{c.name}</span>
                      {c.subtitle && <span className="text-xs text-muted-foreground ml-2 italic">{c.subtitle}</span>}
                    </span>
                    <span className="text-xs text-muted-foreground">{c.set_name} · {c.rarity}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-10 rounded-lg border border-border p-4 text-sm text-muted-foreground">
          <p>
            See also: <Link to="/rotation" className="underline">Lorcana rotation</Link> ·{' '}
            <Link to="/sets" className="underline">all sets</Link>.
          </p>
        </section>
      </article>
    </div>
  );
}
